import api, { formatApiError } from "@/lib/api";
import { supportsDirectoryPicker } from "@/lib/cfdiExportFolder";

export const REPOSITORY_RELOCATE_MESSAGE =
  "La ruta de exportación local 'BNT.TaxRepository' ya no existe en el disco. " +
  "Los CFDI siguen en la base de datos; indique una nueva carpeta solo si desea volver a exportar o descargar archivos ahí.";

/**
 * Abre el selector nativo de carpeta (File System Access API) o solicita la ruta absoluta
 * en el equipo donde corre el backend (instalación local / red).
 */
export async function pickClientRepositoryRootPath() {
  const promptMessage =
    "Indique la ruta ABSOLUTA donde el servidor creará la carpeta BNT.TaxRepository.\n\n" +
    "Ejemplos:\nC:\\Fiscal\\Datos\nD:\\Contabilidad\n\\\\SERVIDOR\\Compartido\\Fiscal";

  if (supportsDirectoryPicker()) {
    let handle;
    try {
      handle = await window.showDirectoryPicker({ mode: "readwrite" });
    } catch (e) {
      if (e?.name === "AbortError") {
        throw new Error("Selección de carpeta cancelada.");
      }
      throw e;
    }
    const hint = handle?.name || "";
    const path = window.prompt(
      hint
        ? `Carpeta seleccionada: «${hint}».\n\n${promptMessage}`
        : promptMessage,
      "",
    );
    const trimmed = String(path || "").trim();
    if (!trimmed) {
      throw new Error("Debe indicar la ruta absoluta de la carpeta base.");
    }
    return trimmed;
  }

  const path = window.prompt(
    "Seleccione la ubicación base donde el sistema creará BNT.TaxRepository.\n\n" + promptMessage,
    "",
  );
  const trimmed = String(path || "").trim();
  if (!trimmed) {
    throw new Error("Debe indicar la ruta absoluta de la carpeta base.");
  }
  return trimmed;
}

/** Consulta si el despliegue gestiona BNT.TaxRepository en el servidor (Railway/Docker). */
export async function fetchRepositoryMode() {
  const { data } = await api.get("/system/repository-mode", { timeout: 15000 });
  return data;
}

/** Consulta la configuración persistida del cliente (validación silenciosa). */
export async function fetchClientRepositoryConfig(clientId) {
  if (!clientId) return null;
  const { data } = await api.get(`/clients/${clientId}/repository-config`, { timeout: 30000 });
  return data;
}

/** Restablece la ruta de BNT.TaxRepository para un cliente existente. */
export async function saveClientRepositoryConfig(clientId, rootPath) {
  const { data } = await api.put(`/clients/${clientId}/repository-config`, {
    root_path: rootPath,
  });
  return data;
}

/**
 * Botón «Generar / Verificar Repositorio Local»: pide ubicación, crea subcarpetas del RFC y persiste en MongoDB.
 */
export async function generateOrVerifyLocalRepository(clientId) {
  if (!clientId) {
    throw new Error("Seleccione un cliente.");
  }
  const rootPath = await pickClientRepositoryRootPath();
  return saveClientRepositoryConfig(clientId, rootPath);
}

/** Valida y, si hace falta, pide nueva ubicación al usuario. */
export async function ensureClientRepositoryReady(clientId, { onNeedsRelocation } = {}) {
  const cfg = await fetchClientRepositoryConfig(clientId);
  if (!cfg?.needs_relocation) return cfg;
  if (typeof onNeedsRelocation === "function") {
    onNeedsRelocation(cfg);
    return cfg;
  }
  const rootPath = await pickClientRepositoryRootPath();
  try {
    return await saveClientRepositoryConfig(clientId, rootPath);
  } catch (e) {
    throw new Error(formatApiError(e.response?.data?.detail) || "No se pudo restablecer BNT.TaxRepository");
  }
}
